import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError } from '../lib/api.js';
import {
  quoteCreditOrder,
  type CreditOrderQuote,
  type CreditPack,
  type CreditPacksResponse,
  type OrderCostSummary,
  type RateBand,
} from '../lib/creditsApi.js';
import { formatCredits, formatMoney, formatNumber } from '../lib/format.js';
import type { LiveOffer } from '../lib/offer.js';

import { OfferLine } from './OfferLine.js';
import { Alert, Button, Card } from './ui.js';

/**
 * The buy page: sites first. **Operator ruling D-9b, ruling 10, mock v8.**
 *
 * An agency does not want credits. They want sites built, and credits are the
 * accounting behind that. So the page opens with a slider asking how many sites
 * they want, and everything else is a translation of that one number.
 *
 * Three inputs, one order:
 *
 *   - the **slider**, always denominated in builds, thumb at the credit
 *     equivalent of the whole order;
 *   - the **typed field**, in whichever of Sites, Renewals or Credits is
 *     selected;
 *   - the **pack steppers**.
 *
 * They stay in sync because they are three views of a single number of credits,
 * and **the last one touched owns it** (ruling 21). The slider and the typed
 * field each take the whole order and clear the packs; the packs take the whole
 * order and are mirrored by the other two. They used to sum, which is how a
 * Scale pack and a leftover 1 in the field became 11 builds with nothing on the
 * page accounting for the extra one.
 *
 * **Adaptive words, fixed structure.** The selected unit changes what the
 * number means and the language of the bubble, the hint and the summary
 * translation. It does not change the layout, the slider's denomination or the
 * cards, because a page that rearranges itself under someone choosing a unit is
 * a page they stop trusting.
 *
 * The rule this component still follows without exception: **it does no
 * pricing.** Every figure on screen comes from the server's quote or the
 * catalogue. The engine has one home, and a buy page that re-derived a rate
 * would eventually advertise one the checkout would not honour.
 */

/** How long to wait after typing before asking for a new quote. */
const QUOTE_DEBOUNCE_MS = 250;

/** The slider's range, in builds. Past the top the page stops quoting. */
const MIN_SLIDER_BUILDS = 1;
const MAX_SLIDER_BUILDS = 50;

/**
 * The slider thumb's width, in pixels - D-9b ruling 19.
 *
 * **This has to be a number this file knows**, which is why the thumb is styled
 * rather than left native. A tick at `x%` of the track's width does not sit
 * under the thumb at that value: the thumb has width, so its centre travels
 * only from half a thumb in to half a thumb short of the end. Ticks laid out on
 * the naive percentage drift outwards towards both ends, worst at 1 and 50.
 *
 * With the width known, `tickOffset` corrects for it exactly, and the same
 * constant styles the thumb via a custom property so the two can never
 * disagree. A native thumb whose width varies by browser could not be lined up
 * at all.
 */
export const SLIDER_THUMB_PX = 20;

/**
 * Where a value sits along the track, as a CSS length.
 *
 * `left: calc(f% + (0.5 - f) * thumb)` puts the point at the centre of the
 * thumb when the thumb is at that value: `f * (width - thumb) + thumb / 2`,
 * rearranged so the width stays a percentage and never needs measuring.
 */
export function tickOffset(builds: number): string {
  const fraction = (builds - MIN_SLIDER_BUILDS) / (MAX_SLIDER_BUILDS - MIN_SLIDER_BUILDS);
  const nudge = (0.5 - fraction) * SLIDER_THUMB_PX;
  return `calc(${(fraction * 100).toFixed(4)}% + ${nudge.toFixed(4)}px)`;
}

/** What the typed field's number means. */
export type OrderUnit = 'sites' | 'renewals' | 'credits';

const UNIT_LABELS: Record<OrderUnit, string> = {
  sites: 'Sites',
  renewals: 'Renewals',
  credits: 'Credits',
};

export interface CreditOrder {
  presetQuantities: Record<string, number>;
  customCredits: number;
  couponCode?: string;
}

export interface CreditOrderBuilderProps {
  catalogue: CreditPacksResponse;
  /** Prefilled amount from the renewal deep link, in credits. */
  initialCustomCredits?: number | undefined;
  onCheckout: (order: CreditOrder) => void;
  busy: boolean;
  /**
   * C-106: the server refused the checkout because an earlier one with this
   * once-per-account code was paid a moment ago. Shown by the code box, like
   * every other coupon refusal, rather than at the top of the page.
   */
  couponRefusal?: string | null;
  /** Called when the buyer applies or removes a code, so the refusal can clear. */
  onCouponChange?: () => void;
  /**
   * C-133. A live offer the sign-up address named, said beside the code box
   * until a code is applied. Never applied for the buyer.
   */
  offer?: LiveOffer | null;
  /** Called once the offer line has actually been on screen. */
  onOfferShown?: () => void;
}

/** What one unit is worth in credits. Zero means the config cannot express it. */
function creditsPerUnit(unit: OrderUnit, costs: OrderCostSummary): number {
  if (unit === 'sites') return costs.siteBuildCredits;
  if (unit === 'renewals') return costs.yearExtensionCredits;
  return 1;
}

/**
 * A credit amount, said in the selected unit - ruling 18.
 *
 * Rounded, because a threshold expressed in a unit that does not divide it
 * evenly has no whole answer, and a tick reading "14.4" would be worse than one
 * reading 15. In the shipped config every threshold divides exactly in all
 * three units.
 */
function inUnit(credits: number, unit: OrderUnit, costs: OrderCostSummary): number {
  const size = creditsPerUnit(unit, costs);
  return size > 0 ? Math.round(credits / size) : credits;
}

export function CreditOrderBuilder({
  catalogue,
  initialCustomCredits,
  onCheckout,
  busy,
  couponRefusal = null,
  onCouponChange,
  offer = null,
  onOfferShown,
}: CreditOrderBuilderProps): React.JSX.Element {
  const { orderCosts, bounds, currency } = catalogue;
  const buildCredits = orderCosts.siteBuildCredits;

  const [quantities, setQuantities] = useState<Record<string, number>>({});
  /*
   * Which control last set the order - D-9b ruling 21.
   *
   * The page holds **one** order quantity. The packs, the slider and the typed
   * field are three ways to set it, and this records which of them is currently
   * saying what it is. It starts on `typed` because an empty page has no packs
   * selected and the typed field is where a fresh order goes.
   */
  const [source, setSource] = useState<'packs' | 'typed'>('typed');
  /*
   * Sites on a fresh page - D-9b ruling 22. It is the question the page asks in
   * its own heading, so it is the unit the field should already be in.
   *
   * The `?buy=` deep link is the exception and still opens Credits mode, per
   * ruling 10: a renewal shortfall is a number of credits, not a number of
   * sites, and converting it would round it into something the renewal no
   * longer covers.
   */
  const [unit, setUnit] = useState<OrderUnit>(
    initialCustomCredits === undefined ? 'sites' : 'credits',
  );
  const [amountText, setAmountText] = useState(
    initialCustomCredits === undefined ? '' : String(initialCustomCredits),
  );

  const [couponText, setCouponText] = useState('');
  const [appliedCode, setAppliedCode] = useState('');

  // C-133: the line is on screen from the moment the offer is known to be live.
  useEffect(() => {
    if (offer !== null) onOfferShown?.();
  }, [offer, onOfferShown]);

  const [quote, setQuote] = useState<CreditOrderQuote | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [quoting, setQuoting] = useState(false);

  const unitSize = creditsPerUnit(unit, orderCosts);
  const typedAmount = parseWholeNumber(amountText);
  const typedCredits = unitSize > 0 ? typedAmount * unitSize : 0;

  const creditsOf = useCallback(
    (counts: Record<string, number>) =>
      catalogue.packs.reduce((total, pack) => total + pack.credits * (counts[pack.key] ?? 0), 0),
    [catalogue.packs],
  );
  const presetCredits = useMemo(() => creditsOf(quantities), [creditsOf, quantities]);

  /**
   * The one order quantity - D-9b ruling 21.
   *
   * It used to be `presetCredits + typedCredits`, so a pack and a stray typed
   * number added together. The operator selected Scale, left a 1 in the field
   * and got 11 builds with nothing on the page explaining the extra one. **Two
   * inputs quietly summing is a total nobody asked for**, and the buyer cannot
   * correct a number they cannot see the parts of.
   *
   * Now whichever control was touched last owns the whole order and the others
   * follow it. Packs still sum with each other, because two Growth packs are a
   * deliberate ten builds rather than an accident of two controls.
   */
  const totalCredits = source === 'packs' ? presetCredits : typedCredits;

  /**
   * Where the thumb sits: the whole order, in builds.
   *
   * Rounded, clamped and derived rather than stored, so it can never disagree
   * with the order. Past 50 builds it pins at the top and the page says so
   * rather than quoting a figure it has decided not to publish.
   */
  const sliderBuilds =
    buildCredits > 0
      ? Math.min(
          MAX_SLIDER_BUILDS,
          Math.max(MIN_SLIDER_BUILDS, Math.round(totalCredits / buildCredits)),
        )
      : MIN_SLIDER_BUILDS;

  /**
   * Only the newest quote may land.
   *
   * Without this a slow request for 40 credits can arrive after a fast one for
   * 900 and put the wrong price under the buy button: the one place on this
   * page where being briefly wrong is not acceptable.
   */
  const latestRequest = useRef(0);

  /**
   * The order, built from whichever control owns it.
   *
   * The request shape is unchanged from before ruling 21: a pack order still
   * travels as `presetQuantities` so `payments.pack_key` keeps recording which
   * pack was bought, and a typed or dragged order still travels as
   * `customCredits`. What changed is that **only one of them is ever
   * non-empty**, so the two can no longer sum on the server either.
   */
  const order = useMemo(
    () =>
      source === 'packs'
        ? orderOf(quantities, 0, appliedCode)
        : orderOf({}, typedCredits, appliedCode),
    [source, quantities, typedCredits, appliedCode],
  );

  const refreshQuote = useCallback(async (order: CreditOrder) => {
    const ticket = latestRequest.current + 1;
    latestRequest.current = ticket;

    setQuoting(true);
    try {
      const { quote: fresh } = await quoteCreditOrder(order);
      if (latestRequest.current !== ticket) return;
      setQuote(fresh);
      setQuoteError(null);
    } catch (caught) {
      if (latestRequest.current !== ticket) return;
      setQuote(null);
      setQuoteError(caught instanceof ApiError ? caught.message : 'We could not price that order.');
    } finally {
      if (latestRequest.current === ticket) setQuoting(false);
    }
  }, []);

  useEffect(() => {
    if (totalCredits === 0) {
      latestRequest.current += 1;
      setQuote(null);
      setQuoteError(null);
      setQuoting(false);
      return;
    }

    const timer = setTimeout(() => {
      void refreshQuote(order);
    }, QUOTE_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [order, totalCredits, refreshQuote]);

  /** The order, said in the selected unit, for the field to mirror. */
  function textFor(credits: number): string {
    if (credits === 0 || unitSize <= 0) return '';
    return String(Math.round(credits / unitSize));
  }

  /**
   * A pack stepper: the packs become the whole order - ruling 21.
   *
   * The slider and the typed field mirror the packs' total exactly, so the page
   * shows one quantity three ways rather than three quantities.
   */
  function setQuantity(key: string, next: number): void {
    const nextQuantities = { ...quantities, [key]: Math.max(0, next) };
    setSource('packs');
    setQuantities(nextQuantities);
    setAmountText(textFor(creditsOf(nextQuantities)));
  }

  /**
   * The slider: this value becomes the whole order - ruling 21.
   *
   * It used to take up only the slack left over after the packs, which is how a
   * pack and a typed number came to add up. Dragging now resets every stepper
   * to nothing, **including a drag below a pack already selected**: moving the
   * thumb down is how somebody says they want fewer, and refusing to go below a
   * pack they had clicked earlier would leave them stuck with no way out but a
   * reload.
   */
  function setSliderBuilds(builds: number): void {
    if (buildCredits <= 0 || unitSize <= 0) return;

    setSource('typed');
    setQuantities({});
    setAmountText(textFor(builds * buildCredits));
  }

  /** Typing: this value becomes the whole order, packs cleared - ruling 21. */
  function setTypedAmount(next: string): void {
    setSource('typed');
    setQuantities({});
    setAmountText(next);
  }

  /**
   * Switching unit keeps the order, and re-expresses it in the new words.
   *
   * **Not a change of source.** Choosing Renewals while a pack is selected is
   * asking for different words, not a different order, so the packs stay and
   * the mirrored figure is simply restated.
   */
  function chooseUnit(next: OrderUnit): void {
    const nextSize = creditsPerUnit(next, orderCosts);
    setUnit(next);
    if (nextSize <= 0) return;
    setAmountText(totalCredits === 0 ? '' : String(Math.round(totalCredits / nextSize)));
  }

  const canBuy = quote !== null && !quoting && !busy;

  return (
    <div className="mt-5 space-y-4">
      <ExplainerStrip orderCosts={orderCosts} rackRateMinor={catalogue.rackRateMinor} />

      <div className="lg:grid lg:grid-cols-3 lg:gap-6">
        <div className="space-y-4 lg:col-span-2">
          <SliderCard
            unit={unit}
            amountText={amountText}
            sliderBuilds={sliderBuilds}
            totalCredits={totalCredits}
            orderCosts={orderCosts}
            bands={catalogue.bands}
            currency={currency}
            bounds={bounds}
            quote={quote}
            onUnitChange={chooseUnit}
            onAmountChange={setTypedAmount}
            onSliderChange={setSliderBuilds}
          />

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {catalogue.packs.map((pack) => (
              <PackCard
                key={pack.key}
                pack={pack}
                quantity={quantities[pack.key] ?? 0}
                max={bounds.maxCredits}
                onChange={(next) => {
                  setQuantity(pack.key, next);
                }}
              />
            ))}
          </div>
        </div>

        <OrderSummary
          quote={quote}
          quoting={quoting}
          error={quoteError}
          totalCredits={totalCredits}
          unit={unit}
          currency={currency}
          taxNotice={catalogue.taxNotice}
          minCredits={bounds.minCredits}
          orderCosts={orderCosts}
          busy={busy}
          canBuy={canBuy}
          offer={offer}
          couponText={couponText}
          onCouponTextChange={setCouponText}
          couponRefusal={couponRefusal}
          onApplyCoupon={() => {
            setAppliedCode(couponText.trim());
            onCouponChange?.();
          }}
          onClearCoupon={() => {
            setCouponText('');
            setAppliedCode('');
            onCouponChange?.();
          }}
          onCheckout={() => {
            onCheckout(order);
          }}
        />
      </div>
    </div>
  );
}

/**
 * The request body, built one way.
 *
 * `couponCode` is omitted rather than sent empty when there is none: an empty
 * string is a code nobody typed, and the server would have to decide what that
 * means.
 */
function orderOf(
  presetQuantities: Record<string, number>,
  customCredits: number,
  couponCode: string,
): CreditOrder {
  return {
    presetQuantities,
    customCredits,
    ...(couponCode === '' ? {} : { couponCode }),
  };
}

/**
 * The explainer strip: a white card like its neighbours - D-9b rulings 17, 20.
 *
 * It was a dark band, which gave the page's heaviest visual weight to a glossary
 * line; ruling 17 quietened it. Page-grey then read as a disabled panel rather
 * than part of the page, so **ruling 20 makes it a white card**: same background
 * and border as the cards below it, figures bold. It belongs to the page, it is
 * just not shouting.
 *
 * All three figures come from the server, because two of them are
 * operator-editable and a strip with them written in would start lying quietly.
 */
function ExplainerStrip({
  orderCosts,
  rackRateMinor,
}: {
  orderCosts: OrderCostSummary;
  rackRateMinor: number;
}): React.JSX.Element | null {
  const { siteBuildCredits, yearExtensionCredits } = orderCosts;
  if (siteBuildCredits <= 0 || yearExtensionCredits <= 0) return null;

  return (
    <div
      className="rounded-panel bg-card px-4 py-3 text-center text-sm text-fg-2 shadow-sm ring-1 ring-rule"
      data-testid="credit-explainer"
    >
      A site build is{' '}
      <span className="font-semibold text-fg">{formatCredits(siteBuildCredits)}</span> · a
      year&rsquo;s renewal is{' '}
      <span className="font-semibold text-fg">{formatCredits(yearExtensionCredits)}</span> ·
      1 credit ={' '}
      <span className="font-semibold text-fg">{formatMoney(rackRateMinor, 'usd')}</span>
    </div>
  );
}

/**
 * The slider card: the first thing on the page, and the thing it is about.
 *
 * "How many sites do you want to build?" is the question an agency is actually
 * asking. Everything under it is that answer restated.
 */
function SliderCard({
  unit,
  amountText,
  sliderBuilds,
  totalCredits,
  orderCosts,
  bands,
  currency,
  bounds,
  quote,
  onUnitChange,
  onAmountChange,
  onSliderChange,
}: {
  unit: OrderUnit;
  amountText: string;
  sliderBuilds: number;
  totalCredits: number;
  orderCosts: OrderCostSummary;
  bands: RateBand[];
  currency: string;
  bounds: { minCredits: number; maxCredits: number };
  /** For the headline: the price the buyer will actually pay (ruling 15). */
  quote: CreditOrderQuote | null;
  onUnitChange: (next: OrderUnit) => void;
  onAmountChange: (next: string) => void;
  onSliderChange: (builds: number) => void;
}): React.JSX.Element {
  const { siteBuildCredits, yearExtensionCredits } = orderCosts;

  /** The rungs, as ticks. The rack band has no builds threshold behind it. */
  const points = bands.filter(
    (band): band is RateBand & { minBuilds: number } =>
      band.minBuilds !== null && band.minBuilds <= MAX_SLIDER_BUILDS,
  );
  const deepest = points.length === 0 ? null : points[points.length - 1]!;

  return (
    <Card>
      <h3 className="text-lg font-semibold tracking-tight text-fg">
        How many sites do you want to build?
      </h3>

      <SliderBubble
        unit={unit}
        sliderBuilds={sliderBuilds}
        totalCredits={totalCredits}
        orderCosts={orderCosts}
        currency={currency}
        quote={quote}
      />

      <div className="mt-4">
        <input
          type="range"
          aria-label="How many sites do you want to build?"
          min={MIN_SLIDER_BUILDS}
          max={MAX_SLIDER_BUILDS}
          step={1}
          value={sliderBuilds}
          onChange={(event) => {
            onSliderChange(Number(event.target.value));
          }}
          className="order-slider w-full accent-blue-ink"
          /*
            Two custom properties, one number - D-9b rulings 19 and 24.

            `--slider-fill` is the same `tickOffset` the marks use, so the filled
            track ends exactly where the thumb's centre is and exactly where the
            tick for that value sits. Restoring the fill by any other measure
            would have put it a few pixels away from marks that were just made to
            line up, which is a worse result than having no fill at all.
          */
          style={{
            ['--slider-thumb' as string]: `${String(SLIDER_THUMB_PX)}px`,
            ['--slider-fill' as string]: tickOffset(sliderBuilds),
          }}
          data-testid="sites-slider"
        />

        {/*
          Marked, stacked labels, re-denominated with the selected unit - D-9b
          rulings 18, 19.

          The four positions never move, because they are the same four
          thresholds whichever words are on them. What changes is the number
          above the percentage: the rung that is 5 builds is 15 renewals and 75
          credits, and a page that says "5" while the field is asking for
          renewals is a page that has stopped agreeing with itself.

          Each label carries a faint dash back up to the track, and both the dash
          and the label are positioned by `tickOffset`, which accounts for the
          thumb's width. A number floating under a track with nothing joining it
          to a point is a number the eye has to guess the target of, and a dash
          pointing a few pixels off its own value is worse than no dash: it makes
          a precise-looking claim that is wrong.

          The end tick is placed the same way rather than pinned to the right
          edge, which is what used to leave it drifting away from the thumb at 50.
        */}
        <div className="relative mt-1 h-11" data-testid="slider-ticks">
          {/*
            Deduped: the ladder is operator-editable, and a tier threshold set
            to exactly 50 would otherwise draw two groups on top of each other.
          */}
          {[...new Set([...points.map((band) => band.minBuilds), MAX_SLIDER_BUILDS])].map((builds) => {
            const band = points.find((candidate) => candidate.minBuilds === builds) ?? null;
            const isEnd = builds === MAX_SLIDER_BUILDS;

            return (
              <div
                key={builds}
                className="absolute flex -translate-x-1/2 flex-col items-center"
                style={{ left: tickOffset(builds) }}
                data-testid={isEnd ? 'tick-end-group' : `tick-group-${String(builds)}`}
              >
                <span
                  aria-hidden="true"
                  className="h-1.5 w-px bg-rule-strong"
                  data-testid={`tick-mark-${String(builds)}`}
                />
                <p
                  className="mt-1 text-xs font-semibold text-fg"
                  {...(isEnd ? { 'data-testid': 'tick-end' } : {})}
                >
                  {formatNumber(inUnit(builds * siteBuildCredits, unit, orderCosts))}
                </p>
                {band === null ? null : (
                  <p className="text-xs font-medium whitespace-nowrap text-ok">
                    {band.discountPercent}% off
                    {deepest !== null && band.minBuilds === deepest.minBuilds ? ' · best rate' : ''}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-5 border-t border-rule-soft pt-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <span className="block text-sm font-medium text-fg">Or type an amount</span>
            <div
              className="mt-1.5 inline-flex rounded-box bg-sunken p-0.5"
              role="group"
              aria-label="What the number means"
            >
              {(Object.keys(UNIT_LABELS) as OrderUnit[]).map((candidate) => (
                <button
                  key={candidate}
                  type="button"
                  aria-pressed={unit === candidate}
                  onClick={() => {
                    onUnitChange(candidate);
                  }}
                  className={`rounded-control px-3 py-1.5 text-sm font-medium transition ${
                    unit === candidate
                      ? 'bg-card text-fg shadow-sm'
                      : 'text-fg-2 hover:text-fg'
                  }`}
                >
                  {UNIT_LABELS[candidate]}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="order-amount" className="block text-sm font-medium text-fg">
              {UNIT_LABELS[unit]}
            </label>
            <input
              id="order-amount"
              name="orderAmount"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={amountText}
              onChange={(event) => {
                onAmountChange(event.target.value);
              }}
              placeholder="0"
              className="mt-1.5 block w-32 rounded-control border-0 px-3 py-2 text-fg shadow-sm ring-1 ring-rule-strong ring-inset placeholder:text-fg-faint focus:ring-2 focus:ring-blue-ink focus:ring-inset sm:text-sm"
            />
          </div>
        </div>

        <p className="mt-2 text-sm text-fg-muted" data-testid="unit-hint">
          {unit === 'sites'
            ? `One site build is ${formatCredits(siteBuildCredits)}.`
            : unit === 'renewals'
              ? `One year's renewal is ${formatCredits(yearExtensionCredits)}.`
              : `Between ${formatNumber(bounds.minCredits)} and ${formatNumber(bounds.maxCredits)} credits.`}
        </p>
      </div>

      <VolumeEnquiry />
    </Card>
  );
}

/**
 * The live bubble: the unit the buyer chose, then the price they will pay.
 *
 * **D-9b ruling 15.** The headline used to end with the standard-rate figure,
 * which is not what anybody is being asked to pay and read as the price on a
 * page whose whole point is a discount. It now leads with the payable total and
 * names the discount actually applied, in brackets.
 *
 * Which discount that is depends on what won: the ladder's percentage
 * ordinarily, the code's when a code beat it. Below the first rung nothing is
 * discounted and the bracket is simply absent, because "(with 0% discount)" is
 * a worse sentence than no bracket at all.
 *
 * The money comes from the quote, never from arithmetic here. While a quote is
 * in flight the bubble shows what has been assembled and no price, rather than
 * a figure this component worked out.
 */
function SliderBubble({
  unit,
  sliderBuilds,
  totalCredits,
  orderCosts,
  currency,
  quote,
}: {
  unit: OrderUnit;
  sliderBuilds: number;
  totalCredits: number;
  orderCosts: OrderCostSummary;
  currency: string;
  quote: CreditOrderQuote | null;
}): React.JSX.Element {
  const { siteBuildCredits, yearExtensionCredits } = orderCosts;

  const builds = siteBuildCredits > 0 ? totalCredits / siteBuildCredits : 0;
  const renewals = yearExtensionCredits > 0 ? totalCredits / yearExtensionCredits : 0;

  /*
   * The lead figure only ever claims a whole unit. The fallback rule stands: a
   * quantity that is not a whole number of builds is shown in credits, never as
   * a fraction of a build.
   */
  const lead =
    unit === 'sites'
      ? Number.isInteger(builds) && builds > 0
        ? `${formatNumber(builds)} ${builds === 1 ? 'site build' : 'site builds'}`
        : formatCredits(totalCredits)
      : unit === 'renewals'
        ? Number.isInteger(renewals) && renewals > 0
          ? `${formatNumber(renewals)} ${renewals === 1 ? 'renewal' : 'renewals'}`
          : formatCredits(totalCredits)
        : formatCredits(totalCredits);

  const discount = discountApplied(quote);

  return (
    <p
      className="mt-3 font-display text-2xl font-semibold tracking-tight text-fg"
      data-testid="slider-bubble"
    >
      {totalCredits === 0 ? (
        <span className="text-fg-faint">Choose an amount</span>
      ) : (
        <>
          {lead}
          {unit === 'credits' ? null : (
            <span className="text-fg-faint"> · {formatCredits(totalCredits)}</span>
          )}
          {quote === null ? null : (
            <>
              <span className="text-fg-faint"> · </span>
              {formatMoney(quote.chargeAmountMinor, currency)}
              {discount === null ? null : (
                <span className="text-base font-medium text-ok">
                  {' '}
                  (with {discount.percent}% discount
                  {discount.code === null ? '' : `, ${discount.code}`})
                </span>
              )}
            </>
          )}
        </>
      )}
      <span className="sr-only"> at {formatNumber(sliderBuilds)} builds on the slider</span>
    </p>
  );
}

/**
 * The discount actually applied to this order, if any.
 *
 * Not "the ladder's rate": a winning code replaces it, and the bracket has to
 * name what the buyer is really getting. `null` below the first rung, where
 * nothing is discounted.
 */
function discountApplied(
  quote: CreditOrderQuote | null,
): { percent: number; code: string | null } | null {
  if (quote === null) return null;

  if (quote.coupon !== null && quote.coupon.won) {
    return { percent: quote.coupon.percentOff, code: quote.coupon.code };
  }
  if (quote.rateBand.discountPercent > 0) {
    return { percent: quote.rateBand.discountPercent, code: null };
  }
  return null;
}

/**
 * "Need more than 50 sites?" - D-9b rulings 8, 10 and 16.
 *
 * **No figure.** Deals past the published ladder are negotiated per agency, and
 * printing a number here would either be a promise nobody made or an anchor
 * that undercuts the negotiation. The page says the door is open and nothing
 * else.
 *
 * It was a `mailto:` until ruling 16. A mailto puts the message in the buyer's
 * own mail client, where nobody can see whether it was ever sent, and where an
 * agency on a machine with no mail client configured gets nothing at all when
 * they click. It opens the Contact page now, with the category prefilled.
 */
function VolumeEnquiry(): React.JSX.Element {
  return (
    <p className="mt-5 border-t border-rule-soft pt-4 text-sm text-fg-2">
      Need more than {formatNumber(MAX_SLIDER_BUILDS)} sites?{' '}
      <Link
        to="/contact?about=volume_order"
        className="font-medium text-fg underline"
        data-testid="volume-enquiry"
      >
        Talk to us about volume pricing.
      </Link>
    </p>
  );
}

/**
 * A pack card, in Option C row order.
 *
 * name · "N site builds" headline · green credits · "$X a build · $Y total" ·
 * savings line. Every figure arrives already worked out; nothing here divides
 * anything. A pack is a whole number of builds by construction.
 */
function PackCard({
  pack,
  quantity,
  max,
  onChange,
}: {
  pack: CreditPack;
  quantity: number;
  max: number;
  onChange: (next: number) => void;
}): React.JSX.Element {
  /** No stepper may offer a quantity the order bounds would refuse. */
  const ceiling = Math.floor(max / pack.credits);

  return (
    <div
      className={`relative flex flex-col rounded-panel bg-card p-5 shadow-sm ring-1 ${
        pack.featured ? 'ring-2 ring-blue-ink' : 'ring-rule'
      }`}
    >
      {pack.featured ? (
        <span className="absolute -top-2.5 left-5 rounded-full bg-blue-deep px-2.5 py-0.5 text-xs font-semibold text-blue-bright">
          Most popular
        </span>
      ) : null}

      <h3 className="text-base font-semibold text-fg">{pack.name}</h3>

      <p className="mt-1 text-lg font-semibold text-fg" data-testid={`site-count-${pack.key}`}>
        {formatNumber(pack.builds)} {pack.builds === 1 ? 'site build' : 'site builds'}
      </p>

      <p className="mt-0.5 text-sm font-medium text-ok" data-testid={`builds-${pack.key}`}>
        {formatCredits(pack.credits)}
      </p>

      <p className="mt-2 text-sm text-fg-2" data-testid={`per-build-${pack.key}`}>
        {formatMoney(pack.perBuildAmountMinor, pack.currency)} a build ·{' '}
        {formatMoney(pack.amountMinor, pack.currency)} total
      </p>

      {pack.savingMinor > 0 ? (
        <p
          className="mt-0.5 text-sm font-medium text-ok"
          data-testid={`saving-${pack.key}`}
        >
          {pack.discountPercent}% off · save {formatMoney(pack.savingMinor, pack.currency)}
        </p>
      ) : (
        <p className="mt-0.5 text-sm text-fg-muted" data-testid={`saving-${pack.key}`}>
          {formatMoney(pack.perBuildAmountMinor, pack.currency)} a build · standard rate
        </p>
      )}

      <Stepper
        label={`${pack.name} packs`}
        value={quantity}
        max={ceiling}
        onChange={onChange}
        className="mt-4"
      />
    </div>
  );
}

function Stepper({
  label,
  value,
  max,
  onChange,
  className = '',
}: {
  label: string;
  value: number;
  max: number;
  onChange: (next: number) => void;
  className?: string;
}): React.JSX.Element {
  return (
    <div className={`mt-auto flex items-center gap-3 pt-4 ${className}`}>
      <Button
        type="button"
        variant="secondary"
        aria-label={`One fewer ${label}`}
        disabled={value === 0}
        className="h-9 w-9 !px-0"
        onClick={() => {
          onChange(value - 1);
        }}
      >
        −
      </Button>
      <input
        type="text"
        inputMode="numeric"
        aria-label={label}
        value={String(value)}
        onChange={(event) => {
          onChange(parseWholeNumber(event.target.value));
        }}
        className="w-14 rounded-control border-0 px-2 py-1.5 text-center text-fg shadow-sm ring-1 ring-rule-strong ring-inset focus:ring-2 focus:ring-blue-ink focus:ring-inset sm:text-sm"
      />
      <Button
        type="button"
        variant="secondary"
        aria-label={`One more ${label}`}
        disabled={value >= max}
        className="h-9 w-9 !px-0"
        onClick={() => {
          onChange(value + 1);
        }}
      >
        +
      </Button>
    </div>
  );
}

/**
 * The unit-led translation box.
 *
 * Same structure whichever unit is selected, different leading sentence. In
 * Renewals mode ruling 10 asks for "Renews 12 sites for a year · or covers up
 * to 4 site builds", and the two figures are the server's: both are rounded
 * down, because "up to" is a promise the balance has to keep.
 */
function UnitTranslation({
  quote,
  unit,
  orderCosts,
}: {
  quote: CreditOrderQuote;
  unit: OrderUnit;
  orderCosts: OrderCostSummary;
}): React.JSX.Element | null {
  const builds = quote.siteBuildsCovered;
  const renewals = quote.renewalsCovered;

  if (orderCosts.siteBuildCredits <= 0) return null;
  if (builds < 1 && renewals < 1) return null;

  const buildsPhrase = `${formatNumber(builds)} ${builds === 1 ? 'site build' : 'site builds'}`;
  const renewalsPhrase = `${formatNumber(renewals)} ${renewals === 1 ? 'site' : 'sites'}`;

  return (
    <p className="rounded-box bg-subtle px-3 py-2 text-sm text-fg-2" data-testid="builds-summary">
      {unit === 'renewals' ? (
        <>
          Renews <span className="font-medium text-fg">{renewalsPhrase}</span> for a year ·
          or covers up to <span className="font-medium text-fg">{buildsPhrase}</span>
        </>
      ) : (
        <>
          Covers up to <span className="font-medium text-fg">{buildsPhrase}</span> · or
          renews <span className="font-medium text-fg">{renewalsPhrase}</span> for a year
        </>
      )}
    </p>
  );
}

function OrderSummary({
  quote,
  quoting,
  error,
  totalCredits,
  unit,
  currency,
  taxNotice,
  minCredits,
  orderCosts,
  busy,
  canBuy,
  offer,
  couponText,
  couponRefusal,
  onCouponTextChange,
  onApplyCoupon,
  onClearCoupon,
  onCheckout,
}: {
  quote: CreditOrderQuote | null;
  quoting: boolean;
  error: string | null;
  totalCredits: number;
  unit: OrderUnit;
  currency: string;
  /** What the total excludes, from the server (D-13). */
  taxNotice: string;
  minCredits: number;
  orderCosts: OrderCostSummary;
  busy: boolean;
  canBuy: boolean;
  offer: LiveOffer | null;
  couponText: string;
  couponRefusal: string | null;
  onCouponTextChange: (next: string) => void;
  onApplyCoupon: () => void;
  onClearCoupon: () => void;
  onCheckout: () => void;
}): React.JSX.Element {
  /*
   * C-133. Beside the code box, where the buyer types it, and before there is
   * a code box, under the prompt to choose a size. Gone once a code is
   * applied: the applied card says what it did.
   */
  const offerLine = offer !== null && (quote?.coupon ?? null) === null ? <OfferLine offer={offer} /> : null;

  return (
    <div className="mt-4 lg:mt-0">
      <Card className="lg:sticky lg:top-6">
        <h3 className="text-base font-semibold text-fg">Your order</h3>

        {totalCredits === 0 ? (
          <>
            <p className="mt-3 text-sm text-fg-muted">
              Choose a size above to see the price.
            </p>
            {offerLine ? <div className="mt-3">{offerLine}</div> : null}
          </>
        ) : error !== null ? (
          <div className="mt-3">
            <Alert>{error}</Alert>
          </div>
        ) : quote === null ? (
          <p className="mt-3 text-sm text-fg-muted" role="status">
            Pricing…
          </p>
        ) : (
          <div className="mt-3 space-y-3" aria-busy={quoting || undefined}>
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm text-fg-2">Credits</span>
              <span className="text-sm font-medium text-fg font-mono tabular-nums">
                {formatNumber(quote.credits)}
              </span>
            </div>

            {/*
              The rate actually being paid, not the ladder's. A winning coupon
              used to leave this claiming the ladder rate the order did not get.
              `effectiveRateMinor` is the charge over the credits, true
              whichever candidate won, and the label says which one it is.
            */}
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm text-fg-2">
                {quote.coupon?.won === true ? 'Your rate' : 'Rate'}
              </span>
              <span
                className="text-sm font-medium text-fg font-mono tabular-nums"
                data-testid="summary-rate"
              >
                {formatMoney(quote.effectiveRateMinor, currency)} a credit
              </span>
            </div>

            {/*
              The translation sits directly under Rate - D-9b ruling 23.

              It answers "what do I get", and that question belongs beside the
              quantity and the rate rather than after the money. Between the
              struck-through standard rate and the total it also broke the one
              comparison the summary is built to make: the eye should fall from
              the crossed-out figure straight onto the payable one.
            */}
            <UnitTranslation quote={quote} unit={unit} orderCosts={orderCosts} />

            {/*
              Standard rate first, then the total - D-9b ruling 15.

              A single Total row states a price without saying what it is
              cheaper than, which on a page built around a discount is the one
              thing worth showing. Ordered so the eye falls down from the
              crossed-out figure to the payable one.

              When nothing is saved the row disappears rather than repeating the
              total, because "Standard rate $150.00 / Total $150.00" reads as a
              mistake.
            */}
            {quote.savingMinor > 0 ? (
              <div className="flex items-baseline justify-between gap-3 border-t border-rule-soft pt-3">
                <span className="text-sm text-fg-2">Standard rate</span>
                <span
                  className="text-sm text-fg-muted line-through font-mono tabular-nums"
                  data-testid="summary-standard"
                >
                  {formatMoney(quote.rackAmountMinor, currency)}
                </span>
              </div>
            ) : null}

            <div
              className={`flex items-baseline justify-between gap-3 ${
                quote.savingMinor > 0 ? '' : 'border-t border-rule-soft pt-3'
              }`}
            >
              <span className="text-sm font-medium text-fg">Total</span>
              <span
                className="text-2xl font-semibold tracking-tight text-fg font-display tabular-nums"
                data-testid="summary-total"
              >
                {formatMoney(quote.chargeAmountMinor, currency)}
              </span>
            </div>

            {quote.savingMinor > 0 ? (
              <p className="text-sm font-medium text-ok" data-testid="summary-saving">
                You save {formatMoney(quote.savingMinor, currency)} on the standard rate
              </p>
            ) : null}

            {/*
              What the total does not include - D-13, item 1c.

              Directly under the figure, because that is where the promise is
              made. A UK buyer used to read "$50.00 Total" here and be asked for
              $60.00 on the next screen, and a price that rises twenty percent
              between the page and the payment step loses the sale.

              The sentence comes from the server, which is also where the
              payment page gets it, so the two cannot drift. It is true under
              this entity and under the US one that follows: it promises that
              whatever applies where you are is added, not that anything is.
            */}
            <p className="text-sm text-fg-muted" data-testid="summary-tax-notice">
              {taxNotice}
            </p>

            {/* The lift, disclosed where the buyer decides, never first met on
                the payment page. */}
            {quote.lifted ? (
              <Alert tone="success">
                {formatCredits(quote.credits)} · cheaper than the{' '}
                {formatNumber(quote.requestedCredits)} you entered.
              </Alert>
            ) : null}

            <BuildsNudge quote={quote} orderCosts={orderCosts} />

            <div>
              {offerLine ? <div className="mb-3">{offerLine}</div> : null}
              <CouponField
                quote={quote}
                currency={currency}
                value={couponText}
                onChange={onCouponTextChange}
                onApply={onApplyCoupon}
                onClear={onClearCoupon}
              />
              {couponRefusal ? (
                <p
                  className="mt-1.5 text-sm text-danger"
                  role="alert"
                  data-testid="checkout-coupon-refusal"
                >
                  {couponRefusal}
                </p>
              ) : null}
            </div>

            <Button className="w-full" busy={busy} disabled={!canBuy} onClick={onCheckout}>
              Buy {formatCredits(quote.credits)}
            </Button>
          </div>
        )}

        {totalCredits > 0 && totalCredits < minCredits && error === null && quote === null ? (
          <p className="mt-3 text-sm text-fg-muted">
            The smallest order is {formatNumber(minCredits)} credits.
          </p>
        ) : null}
      </Card>
    </div>
  );
}

/**
 * The nudge, made builds-aware - D-9b ruling 10.
 *
 * Below the top rung it still says what reaching the next one costs. At or past
 * it there is nothing left to nudge towards on the published ladder, so it says
 * so plainly and points at the same conversation the slider does. **No figure**:
 * what a bulk deal is worth is negotiated, not printed.
 */
function BuildsNudge({
  quote,
  orderCosts,
}: {
  quote: CreditOrderQuote;
  orderCosts: OrderCostSummary;
}): React.JSX.Element | null {
  if (quote.nextBand !== null) {
    return (
      <p className="text-sm text-fg-muted" data-testid="nudge">
        Add {formatNumber(quote.nextBand.addCredits)} credits to reach {quote.nextBand.discountPercent}
        % off.
      </p>
    );
  }

  if (orderCosts.siteBuildCredits <= 0) return null;

  return (
    <p className="text-sm text-fg-2" data-testid="best-rate">
      That&rsquo;s our best online rate.{' '}
      <Link to="/contact?about=volume_order" className="font-medium text-fg underline">
        Talk to us about volume pricing.
      </Link>
    </p>
  );
}
/**
 * "Have a code?" — D-8 rulings 6 and 7.
 *
 * Three states, and each says something different:
 *
 *   - **applied and winning**: the code set the price, and by how much;
 *   - **applied but beaten**: the code is valid and the volume rate was
 *     better. Saying so matters: a buyer who typed a code and saw no change
 *     would otherwise assume it failed;
 *   - **rejected**: the server's plain reason, alongside a still-buyable
 *     order. A bad code never blocks a purchase.
 */
function CouponField({
  quote,
  currency,
  value,
  onChange,
  onApply,
  onClear,
}: {
  quote: CreditOrderQuote;
  currency: string;
  value: string;
  onChange: (next: string) => void;
  onApply: () => void;
  onClear: () => void;
}): React.JSX.Element {
  const applied = quote.coupon;

  if (applied !== null) {
    return (
      <div className="rounded-box bg-subtle px-3 py-2.5">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-medium text-fg">{applied.code} applied</span>
          <button
            type="button"
            onClick={onClear}
            className="text-sm text-fg-muted underline hover:text-fg"
          >
            Remove
          </button>
        </div>
        <p className="mt-1 text-sm text-fg-2">
          {applied.won
            ? `${String(applied.percentOff)}% off · ${formatMoney(applied.discountMinor, currency)} less than the standard rate for this size.`
            : `Your volume rate of ${formatMoney(quote.rateBand.ratePerCreditMinor, currency)} a credit is the better price, so that is what you are paying.`}
        </p>
      </div>
    );
  }

  return (
    <div>
      <label htmlFor="coupon-code" className="block text-sm font-medium text-fg">
        Have a code?
      </label>
      <div className="mt-1.5 flex gap-2">
        <input
          id="coupon-code"
          name="couponCode"
          type="text"
          autoComplete="off"
          autoCapitalize="characters"
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              onApply();
            }
          }}
          className="block w-full rounded-control border-0 px-3 py-2 text-fg uppercase shadow-sm ring-1 ring-rule-strong ring-inset placeholder:normal-case placeholder:text-fg-faint focus:ring-2 focus:ring-blue-ink focus:ring-inset sm:text-sm"
        />
        <Button type="button" variant="secondary" disabled={value.trim() === ''} onClick={onApply}>
          Apply
        </Button>
      </div>

      {quote.couponRejected !== null ? (
        <p className="mt-1.5 text-sm text-danger">{quote.couponRejected.message}</p>
      ) : null}
    </div>
  );
}


/**
 * A whole number from a text field, or 0.
 *
 * A text input rather than `type="number"`: number inputs let a browser hand
 * back "1e5" and "1.5" and silently coerce them. Reading digits only means what
 * the field shows and what is quoted are always the same figure, which is the
 * WAIT-D9-2 lesson applied to the one field a buyer types into.
 */
function parseWholeNumber(text: string): number {
  const digits = text.replace(/[^0-9]/g, '');
  if (digits === '') return 0;
  const value = Number(digits);
  return Number.isSafeInteger(value) ? value : 0;
}
